import { Card, CardContent, Paper, Typography } from "@mui/material";
import { Maybe, Post } from "api";
import React, { forwardRef, ReactNode, useState } from "react";
import styled from "@emotion/styled";
import { Link as ReactLink } from "react-router-dom";
import { PostBottomActions } from "./PostBottomActions";
import { PollComponent } from "./PollComponent";
import CommentComment, { CommentsContainer } from "components/CommentComponent";
import TextPost from "components/TextPost";
import PostDetailsMedia from "./PostDetailsMedia";
import PostDisclaimer from "components/PostDisclaimer";
import PostHeader from "./PostHeader";
import MediaPostNew from "./MediaPostNew";
import SharedPostComponent from "./SharedPostComponent";
import ImagePost from "./ImagePost";
import Pin from "./Pin";
import ImagePostNew from "./ImagePostNew";
import AvatarImage from "./AvatarImage";

interface PostComponentProps {
  post: Maybe<Post> | undefined;
  children?: ReactNode;
  postHeaderComponent?: ReactNode;
  postCountComponent?: ReactNode;
  isPostDetails?: boolean;
  isProfilePosts?: boolean;
  isPinnedPost?: boolean;
}

const PostComponent = forwardRef<HTMLDivElement, PostComponentProps>(
  (
    {
      post,
      children,
      postHeaderComponent,
      postCountComponent,
      isPostDetails = false,
      isProfilePosts = false,
      isPinnedPost = false
    },
    ref
  ) => {
    const [show, setShow] = useState(!Boolean(post?.isSuspected));
    return (
      <CustomCard variant="outlined" ref={ref}>
        {isPinnedPost && <Pin />}
        {postHeaderComponent}
        <PostHeader
          post={post}
          isPostDetails={isPostDetails}
          isProfilePosts={isProfilePosts}
        />
        {show ? (
          <>
            {isPostDetails ? (
              <CardContent>
                <TextPost text={post?.textV2} />
              </CardContent>
            ) : (
              <CardContent>
                <Link to={`/posts/${post?.postId}`} aria-label="Read post details">
                  <TextPost text={post?.textV2} />
                </Link>
              </CardContent>
            )}
            {isPostDetails ? (
              <>
                {post?.meta?.isAvatar ? <AvatarImage media={post?.media} /> : <ImagePostNew media={post?.media!} />}
                <PostDetailsMedia media={post?.media!} />
              </>
            ) : (
              <>
                {post?.meta?.isAvatar ? <Link to={`/posts/${post?.postId}`}><AvatarImage media={post?.media} /></Link> : <ImagePost media={post?.media} to={`/posts/${post?.postId}`} />}
                <MediaPostNew post={post} isPostDetails={isPostDetails} />
              </>
            )}
            {post?.hasPoll && post?.getPoll && <PollComponent post={post} />}
            {Boolean(post?.sharePostId) && (
              <SharedPostComponent post={post?.getSharedPost} />
            )}
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              {postCountComponent}
              <PostDisclaimer />
            </CardContent>
            <PostBottomActions
              post={post}
              isCommentExists={Boolean(post?.comments?.length) || isPostDetails}
            />
            {children}
            {Number(post?.comments?.length) > 0 && (
              <CommentsContainer>
                {post?.comments?.map((comment) => (
                  <CommentComment
                    key={comment?.commentId}
                    post={post}
                    comment={comment}
                  />
                ))}
                {post?.topCommentId && Number(post?.commentcount) > 1 ?
                  <Div>
                    <Typography
                      to={`/posts/${post?.postId}`}
                      aria-label="View all comments"
                      style={{ fontSize: "0.825rem" }}
                      component={Link}
                    >View All Comments</Typography>
                  </Div> : null}
              </CommentsContainer>
            )}

          </>
        ) : (
          <OffensiveContent>
            <p>This post may contain potentially offensive content.</p>
            <a onClick={() => setShow(true)}>View Post</a>
          </OffensiveContent>
        )}
      </CustomCard>
    );
  }
);

export default PostComponent;

const Div = styled("div")({
  textAlign: 'center',
  "@media (min-width: 540px)":{
    textAlign: 'left'
  }
})

const CustomCard = styled(Card)`
  margin-top: 16px;
  &:last-child {
    margin-bottom: 16px;
  }
  .MuiCardActions-root {
    .MuiButton-root {
      flex: 0 1 100%;
    }
  }
  @media (max-width: 350px) {
    margin-top: 0px;
  }
`;


export const PostsContainer = styled.div`
  max-width: 550px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  position: relative;
`;

const OffensiveContent = styled(Paper)`
  line-height: 22px;
  color: #5c7082;
  font-family: "Trebuchet MS", sans-serif;
  font-size: 15px;
  margin-bottom: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  border-radius: 4px;
  margin: 16px;
  a,
  p {
    display: inline;
    text-align: center;
  }
  p {
    color: #536471;
    margin-right: 5px;
  }
  // background-color: #eff1fe;
  a {
    color: #2196f3;
    cursor: pointer;
    white-space: nowrap;
    margin-left: 5px;
  }
`;

const Link = styled(ReactLink)`
  text-decoration: none;
`;
